"use client";

import { createContext, useContext, useState, useCallback, ReactNode } from "react";

interface CommsPanelState {
  isOpen: boolean;
  isPinned: boolean;
  activeLeadId: string | null;
  activeLeadName: string | null;
  activeTab: "timeline" | "copilot";
  panelWidth: number;
  initialComposer: string | null;
  composerSeq: number;
  timelineRefreshSeq: number;
  leadChangedSeq: number;
}

interface CommsContextValue extends CommsPanelState {
  openPanel: (leadId: string, leadName: string, opts?: { composer?: string }) => void;
  closePanel: () => void;
  togglePin: () => void;
  setActiveTab: (tab: "timeline" | "copilot") => void;
  setPanelWidth: (width: number) => void;
  refreshTimeline: () => void;
  notifyLeadChanged: () => void;
}

const CommsContext = createContext<CommsContextValue | null>(null);

export function useCommsPanel() {
  const ctx = useContext(CommsContext);
  if (!ctx) throw new Error("useCommsPanel must be used within CommsProvider");
  return ctx;
}

export function CommsProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<CommsPanelState>({
    isOpen: false,
    isPinned: true,
    activeLeadId: null,
    activeLeadName: null,
    activeTab: "timeline",
    panelWidth: 440,
    initialComposer: null,
    composerSeq: 0,
    timelineRefreshSeq: 0,
    leadChangedSeq: 0,
  });

  const openPanel = useCallback((leadId: string, leadName: string, opts?: { composer?: string }) => {
    setState((s) => ({
      ...s,
      isOpen: true,
      isPinned: true,
      activeLeadId: leadId,
      activeLeadName: leadName,
      activeTab: "timeline",
      initialComposer: opts?.composer ?? null,
      composerSeq: s.composerSeq + 1,
    }));
  }, []);

  const closePanel = useCallback(() => {
    setState((s) => ({ ...s, isOpen: false, activeLeadId: null, activeLeadName: null }));
  }, []);

  const togglePin = useCallback(() => {
    setState((s) => ({ ...s, isPinned: !s.isPinned }));
  }, []);

  const setActiveTab = useCallback((tab: "timeline" | "copilot") => {
    setState((s) => ({ ...s, activeTab: tab }));
  }, []);

  const setPanelWidth = useCallback((width: number) => {
    setState((s) => ({ ...s, panelWidth: Math.max(360, Math.min(width, window.innerWidth * 0.5)) }));
  }, []);

  const refreshTimeline = useCallback(() => {
    setState((s) => ({ ...s, timelineRefreshSeq: s.timelineRefreshSeq + 1 }));
  }, []);

  const notifyLeadChanged = useCallback(() => {
    setState((s) => ({ ...s, leadChangedSeq: s.leadChangedSeq + 1 }));
  }, []);

  return (
    <CommsContext.Provider value={{ ...state, openPanel, closePanel, togglePin, setActiveTab, setPanelWidth, refreshTimeline, notifyLeadChanged }}>
      {children}
    </CommsContext.Provider>
  );
}
